import { isAxiosError } from "axios";
import i18n, { t } from "i18next";
import axios from "../lib/axios";
import { RoleEnum } from "../types";
import { getProjectsAvailableForUser } from "./projects";
import { getRoles } from "./roles";

export const generateRecipients = (
  user: {
    roles?: string[];
    employeeId?: number;
    companyId?: number;
  },
  companyId?: number,
) => {
  return new Promise<{ label: string; value: string }[]>((resolve, reject) => {
    Promise.allSettled([
      getRoles(),
      getProjectsAvailableForUser(companyId),
    ]).then((results) => {
      if (
        results[0].status === "fulfilled" &&
        results[1].status === "fulfilled"
      ) {
        const roles = results[0].value;
        const projects = results[1].value;

        let recipients = [
          ...roles.map((role) => ({
            label: t("announcements.common.userWithRole", { role: role.name }),
            value: `role-${role.id}`,
            name: role.name,
          })),
          ...projects.map((project) => ({
            label: t("announcements.common.projectParticipant", {
              project: project.acronym,
            }),
            value: `project-${project.id}`,
          })),
        ];

        if (user.roles?.includes(RoleEnum.Kierownik)) {
          recipients = recipients.filter(
            (item) =>
              item.value.startsWith("project-") ||
              (item as any).name === RoleEnum.Kierownik,
          );
        }

        resolve(recipients);
      } else {
        reject(new Error(t("common.errorOccurredWhileDataFetching")));
      }
    });
  });
};

export const createAnnouncement = async ({
  title,
  content,
  recipients,
  files,
}: {
  title: string;
  content: string;
  recipients: string[];
  files: File[];
}) => {
  const data = new FormData();
  data.append("title", title);
  data.append("content", content);

  for (const recipient of recipients) {
    if (recipient.startsWith("project-")) {
      data.append("projectIds", recipient.replace("project-", ""));
    }

    if (recipient.startsWith("role-")) {
      data.append("roleIds", recipient.replace("role-", ""));
    }
  }

  for (const file of files) {
    data.append("files", file);
  }

  try {
    await axios.post("/api/announcements", data, {
      headers: {
        "Content-Type": "multipart/form-data",
        "Accept-Language": i18n.language,
      },
    });
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
};

export const updateAnnouncement = async (
  id: number,
  {
    title,
    content,
    recipients,
    files,
    filesToRemove,
  }: {
    title: string;
    content: string;
    recipients: string[];
    files: File[];
    filesToRemove?: number[];
  },
) => {
  const data = new FormData();
  data.append("title", title);
  data.append("content", content);

  for (const recipient of recipients) {
    if (recipient.startsWith("project-")) {
      data.append("projectIds", recipient.replace("project-", ""));
    }

    if (recipient.startsWith("role-")) {
      data.append("roleIds", recipient.replace("role-", ""));
    }
  }

  for (const file of files) {
    data.append("files", file);
  }

  filesToRemove?.forEach((fileId) => {
    data.append("filesToRemove", fileId.toString());
  });

  try {
    await axios.put(`/api/announcements/${id}`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
        "Accept-Language": i18n.language,
      },
    });
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    return {
      isOk: false,
      error: message,
    };
  }
};
